<template>
    <authenticated-layout>
        <v-card class="px-4">

            <div class="grid grid-cols-7 sm:grid-cols-7 gap-2 pt-4">

                <div class="d-flex align-center col-span-3">
                    <v-text-field
                        v-model="form.search"
                        label="Search"
                        placeholder="Type to search..."
                        variant="outlined"
                        density="compact"
                        persistent-placeholder
                        hide-details
                    />
                </div>

                <div class="d-flex align-center col-span-2">
                    <VueDatePicker
                        :teleport="true"
                        :enable-time-picker="false"
                        text-input v-model="form.datePeriod"
                        format="yyyy-MM-dd"
                        :range="true"
                        model-type="format"
                    />
                </div>

                <div class="d-flex align-center col-span-1">
                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="blue"
                        @click="filter()"
                    >
                        {{ $t("sharedSearchFilter.Filter") }}
                    </v-btn>
                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="grey"
                        @click="resetFilers()"
                    >
                        {{ $t("sharedSearchFilter.Reset") }}
                    </v-btn>
                </div>

                <div class="d-flex align-center col-span-1">
                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="red"
                        target="_blank"
                        @click="downloadOffers()"
                    >
                        <v-icon icon="mdi-download" start></v-icon> Offers CSV
                    </v-btn>
                </div>
            </div>

            <div class="overflow-x-auto mt-4">
                <table class="zebra-table">
                    <tr>
                        <th class="text-left w-10">
                            <input
                                type="checkbox"
                                v-model="selectAll"
                                @change="selectAllItems"
                            />
                        </th>
                        <th class="text-left w-20">
                            id
                        </th>
                        <th class="text-left">
                            {{ $t('customers.guardian') }}
                        </th>
                        <th class="text-left">
                            Total leads
                        </th>
                        <th class="text-left">
                            Leads with solar offers
                        </th>
                        <th class="text-left">
                            Leads with HP offers
                        </th>
                        <th class="text-left">
                            Total solar offers
                        </th>
                        <th class="text-left">
                            Total HP offers
                        </th>
                        <th class="text-left">
                            Leads with any offers [%]
                        </th>
                        <th class="text-left">
                            Won offers
                        </th>
                        <th class="text-left">
                            Won offers [%]
                        </th>
                    </tr>

                    <tr
                        v-for="user in users.data"
                        :key="`user-${user.id}`"
                    >
                        <td>
                            <input
                                type="checkbox"
                                v-model="selectedItems"
                                :value="user.id"
                            />
                        </td>
                        <td>{{ user.id }}</td>
                        <td>{{ user.name }}</td>
                        <td>{{ user.total_leads }}</td>
                        <td>{{ user.leads_with_solar_offers }}</td>
                        <td>{{ user.leads_with_hp_offers }}</td>
                        <td>{{ user.total_solar_offers }}</td>
                        <td>{{ user.total_hp_offers }}</td>
                        <td>{{ user.leads_with_any_offers }}</td>
                        <td>{{ user.won_offers }}</td>
                        <td>{{ user.won_offers_percent }}</td>
                    </tr>
                </table>
            </div>

            <ol
                v-if="selectedItems.length > 1"
                class="my-4 text-sm grid grid-cols-6 overflow-hidden rounded-md"
            >
                <li class="p-2 d-flex justify-space-between align-center bg-gray-100">
                    Total leads:
                    <b class="ml-1">{{ getSelectedItemsSum('total_leads') }}</b>
                </li>

                <li class="p-2 ml-2 d-flex justify-space-between align-center bg-gray-100">
                    Leads with solar offers:
                    <b class="ml-1">{{ getSelectedItemsSum('leads_with_solar_offers') }}</b>
                </li>

                <li class="p-2 ml-2 d-flex justify-space-between align-center bg-gray-100">
                    Leads with HP offers:
                    <b>{{ getSelectedItemsSum('leads_with_hp_offers') }}</b>
                </li>

                <li class="p-2 ml-2 d-flex justify-space-between align-center bg-gray-100">
                    Total solar offers:
                    <b>{{ getSelectedItemsSum('total_solar_offers') }}</b>
                </li>

                <li class="p-2 ml-2 d-flex justify-space-between align-center bg-gray-100">
                    Total HP offers:
                    <b>{{ getSelectedItemsSum('total_hp_offers') }}</b>
                </li>

                <li class="p-2 ml-2 d-flex justify-space-between align-center bg-gray-100">
                    Won offers:
                    <b>{{ getSelectedItemsSum('won_offers') }}</b>
                </li>
            </ol>

            <div class="mt-6 pb-6 d-flex align-center">
                <v-select
                    v-model="itemsPerPage"
                    label="Items per page"
                    class="max-w-[150px] mr-5"
                    :items="itemsPerPageOptions"
                    variant="outlined"
                    density="compact"
                    persistent-placeholder
                    hide-details
                />

                <div>
                    <template v-for="(link, k) in users.links" :key="k">
                        <v-btn
                            v-if="link.url === null"
                            size="small"
                            variant="flat"
                            class="mr-1"
                        >
                            {{ link.label }}
                        </v-btn>

                        <v-btn
                            v-else
                            :href="link.url"
                            size="small"
                            variant="flat"
                            class="mr-1"
                            :color="link.active ? 'primary' : ''"
                        >
                            {{ link.label }}
                        </v-btn>
                    </template>
                </div>
            </div>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated.vue";
import pickBy from "lodash/pickBy";
import {th} from "vuetify/locale";

export default {
    components: {
        AuthenticatedLayout,
    },
    props: {
        filters: Object,
        users: {
            type: Object,
            required: true,
        },
        pageLimit: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                salesperson: this.filters.salesperson,
                offerType: this.filters.offerType,
                datePeriod: this.filters.datePeriod,
            },
            selectAll: false,
            selectedItems: [],
            itemsPerPage: this.pageLimit,
            itemsPerPageOptions: [20, 30, 50, 100, 500, 1000],
        };
    },
    watch: {
        itemsPerPage() {
            this.onPageLimitChange();
        },
    },
    methods: {
        downloadOffers() {
            const dateFrom = this.form.datePeriod[0];
            const dateTo = this.form.datePeriod[1];
            window.open(this.route('sales-performance-download-offers', { dateFrom: dateFrom, dateTo: dateTo }), '_blank');
        },
        resetFilers() {
            this.form.search = null;
            this.form.datePeriod = null;

            this.filter();
        },
        filter() {
            this.$inertia.get(this.route('sales-performance-index'), pickBy(this.form), {
                preserveState: true,
            });
        },
        selectAllItems() {
            if (this.selectAll) {
                this.selectedItems = this.users.data.map((item) => item.id);
            } else {
                this.selectedItems = [];
            }
        },
        getSelectedItemsSum(column) {
            return this.selectedItems.reduce((sum, itemId) => {
                const item = this.users.data.find(item => item.id === itemId);

                return sum + parseFloat(item[column]) || 0;
            }, 0);
        },
        onPageLimitChange() {
            this.$inertia.get(this.route('sales-performance-index'), {
                ...pickBy(this.form),
                pageLimit: this.itemsPerPage,
            }, {
                preserveState: true,
            });
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

.zebra-table tr:nth-child(even) {
    background-color: #f2f2f2;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
